import type { Article } from './articleTypes';

export const hybrideRechargeableSansRecharge: Article = {
  slug: 'hybride-rechargeable-sans-recharge-domicile',
  category: 'Motorisations',
  title: 'Hybride rechargeable sans recharge à domicile : bonne ou mauvaise idée ?',
  seoTitle: 'Hybride rechargeable sans recharge à domicile : faut-il éviter ?',
  description:
    'Un hybride rechargeable reste pertinent seulement si vous pouvez recharger souvent. Méthode, cas favorables, coûts cachés et alternatives.',
  intro:
    'Sans recharge régulière, un hybride rechargeable cumule souvent un moteur thermique, une batterie lourde et un prix supérieur sans exploiter son principal avantage. Il existe quelques cas cohérents, mais ils doivent être démontrés par votre routine réelle.',
  image: '/assets/guides/hybride-rechargeable-sans-recharge.webp',
  imageAlt:
    'Un crossover hybride rechargeable garé devant une maison, à côté d’un câble de recharge enroulé',
  publishedAt: '2026-07-20',
  updatedAt: '2026-07-20',
  consultedAt: '20 juillet 2026',
  readingTime: '16 min',
  takeaways: [
    'Sans recharge fréquente, l’hybride rechargeable perd l’essentiel de sa logique économique et énergétique.',
    'Une borne publique occasionnelle ne remplace pas toujours une recharge simple au domicile ou au travail.',
    'Avant de choisir, comptez les jours réellement rechargeables et comparez aussi une essence sobre, une hybride non rechargeable et une électrique.',
  ],
  sections: [
    {
      id: 'reponse-directe',
      title: 'La réponse directe : généralement non, si vous ne rechargez presque jamais',
      content: (
        <>
          <p>
            Un hybride rechargeable, ou PHEV, est conçu pour parcourir une partie des trajets avec
            l’énergie stockée dans sa batterie, puis utiliser son moteur thermique. Lorsque la batterie
            reste vide, la voiture déplace encore cette batterie et toute la chaîne de traction associée.
            L’avantage attendu dépend donc moins de la fiche technique que de la fréquence de recharge.
          </p>
          <aside className="as-article-note as-note-fact">
            <strong>Fait sourcé</strong>
            <p>
              L’ADEME considère l’usage vertueux d’un hybride rechargeable lié à une circulation
              majoritairement électrique et à une recharge systématique. La Commission européenne a
              observé, sur les véhicules immatriculés en 2021, des émissions réelles de CO₂ moyennes
              environ 3,5 fois supérieures aux valeurs d’homologation pour les PHEV, principalement
              parce qu’ils roulaient moins en électrique que prévu.
            </p>
          </aside>
          <p>
            Ces données ne permettent pas de prédire votre consommation individuelle. Elles montrent
            en revanche pourquoi une valeur d’homologation ne doit pas être reprise telle quelle dans un
            budget lorsque la recharge réelle n’est pas connue.
          </p>
        </>
      ),
    },
    {
      id: 'trois-usages',
      title: 'Comprendre les trois voitures cachées dans un hybride rechargeable',
      content: (
        <>
          <p>
            Un même PHEV peut avoir trois comportements très différents. Batterie chargée, il peut assurer
            les trajets courts sans démarrer le moteur thermique. Sur un long parcours, il combine les deux
            chaînes de traction selon sa stratégie de gestion. Batterie vide, il devient surtout une voiture
            thermique qui conserve le poids et la complexité du système électrique.
          </p>
          <p>
            La question n’est donc pas « combien consomme ce modèle ? », mais « dans lequel de ces trois
            états vais-je l’utiliser, et sur quelle part de mes kilomètres ? ». Une consommation relevée lors
            d’un essai de vingt minutes batterie pleine ne décrit pas une année entière. À l’inverse, un essai
            batterie vide ne montre pas le bénéfice que pourrait apporter une recharge quotidienne.
          </p>
          <div className="as-table-scroll" tabIndex={0} aria-label="Les trois situations d’usage d’un hybride rechargeable">
            <table>
              <thead><tr><th>Situation</th><th>Ce qu’il faut mesurer</th><th>Erreur fréquente</th></tr></thead>
              <tbody>
                <tr><td>Batterie chargée</td><td>Autonomie électrique réellement utile selon saison et parcours</td><td>Reprendre l’autonomie annoncée sans marge</td></tr>
                <tr><td>Trajet long</td><td>Consommation après la première portion électrique</td><td>Moyenner seulement les premiers kilomètres</td></tr>
                <tr><td>Batterie vide</td><td>Consommation thermique et agrément sur votre route habituelle</td><td>Utiliser la valeur WLTP pondérée comme budget carburant</td></tr>
              </tbody>
            </table>
          </div>
          <aside className="as-article-note as-note-fact">
            <strong>Ce que montre la donnée européenne</strong>
            <p>
              Le rapport de la Commission européenne porte sur un ensemble de véhicules et non sur un modèle
              précis. Son écart entre homologation et usage réel sert donc d’alerte méthodologique : la part de
              roulage électrique supposée lors de l’homologation doit être remplacée par votre propre scénario.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'recharge-fiable',
      title: 'Une recharge « possible » n’est pas forcément une recharge utilisable',
      content: (
        <>
          <p>
            Je classe une solution de recharge selon quatre critères : disponibilité, fréquence, durée de
            stationnement et prix complet. Une borne devant un supermarché peut être proche mais peu utile si
            vous n’y restez que vingt minutes. Une prise au travail peut être excellente si elle est accessible
            plusieurs jours par semaine, mais fragile si son utilisation dépend d’une place rarement libre.
          </p>
          <p>
            Pendant deux semaines, notez les recharges qui auraient réellement pu avoir lieu. Ne comptez pas
            une borne parce qu’elle apparaît sur une application : comptez seulement une session compatible avec
            un stationnement que vous faisiez déjà. Ajoutez le détour, l’attente éventuelle, la tarification à la
            minute ou au kilowattheure et les jours où la voiture n’est pas au bon endroit.
          </p>
          <ul className="as-article-checklist">
            <li><b>Fiable :</b> domicile ou travail, accès régulier, durée suffisante et coût connu.</li>
            <li><b>Opportuniste :</b> commerce, parking ou salle de sport où vous allez déjà, sans garantie de place.</li>
            <li><b>Dédiée :</b> trajet effectué uniquement pour charger ; son temps et son détour font partie du coût.</li>
            <li><b>Théorique :</b> borne repérée mais jamais testée avec votre badge, vos horaires et le véhicule visé.</li>
          </ul>
          <aside className="as-article-note as-note-opinion">
            <strong>Ma règle de prudence</strong>
            <p>
              Je construis le budget avec les recharges fiables. Les recharges opportunistes améliorent ensuite
              le scénario ; elles ne servent pas à rendre artificiellement cohérent un achat plus cher.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'cas-coherents',
      title: 'Les cas où l’achat peut tout de même rester cohérent',
      content: (
        <>
          <p>
            L’absence de prise privée n’interdit pas automatiquement le PHEV. Une recharge fiable au
            travail, plusieurs jours par semaine, peut couvrir les trajets quotidiens. Une borne publique
            située sur un lieu où vous stationnez déjà longtemps peut également fonctionner, à condition
            que sa disponibilité et son tarif soient acceptables sans organiser votre vie autour d’elle.
          </p>
          <ul className="as-article-checklist">
            <li>Vous pouvez brancher la voiture au moins trois à cinq fois par semaine sans détour dédié.</li>
            <li>La majorité des journées tient dans l’autonomie électrique réellement constatée, hiver compris.</li>
            <li>Les longs trajets restent assez rares pour ne pas dominer la consommation annuelle.</li>
            <li>Le surcoût d’achat et d’assurance reste raisonnable face aux alternatives comparées.</li>
          </ul>
          <aside className="as-article-note as-note-opinion">
            <strong>Mon avis</strong>
            <p>
              Si la recharge dépend d’une borne que vous espérez libre en rentrant le soir, je ne la
              considère pas comme acquise. Je construis d’abord le scénario sans cette borne, puis je
              traite les recharges possibles comme un gain.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'recharge-publique',
      title: 'Recharge publique : intégrer le temps, le tarif et le taux d’échec',
      content: (
        <>
          <p>
            Pour une électrique, une recharge rapide ponctuelle peut débloquer un long trajet. Pour un PHEV,
            la batterie plus petite et la puissance de charge parfois limitée rendent une session dédiée moins
            évidente : l’énergie récupérée doit être mise en face du prix payé et du temps immobilisé. Les
            caractéristiques varient fortement selon les modèles ; il faut vérifier la puissance acceptée en
            courant alternatif et, lorsqu’elle existe, en courant continu.
          </p>
          <p>
            Faites un test avant l’achat avec le réseau que vous comptez utiliser. Relevez le prix final de la
            session, pas seulement le tarif affiché, puis notez l’énergie reçue. Si une recharge demande un détour
            de quinze minutes à l’aller et au retour, ce détour consomme aussi une partie du bénéfice recherché.
          </p>
          <div className="as-article-warning">
            <strong>Les quatre lignes du coût d’une recharge publique</strong>
            <ul>
              <li>énergie facturée et éventuels frais de session ou d’occupation ;</li>
              <li>kilomètres ajoutés pour rejoindre la borne ;</li>
              <li>temps pendant lequel la voiture doit rester branchée ;</li>
              <li>solution de repli lorsque la borne est occupée ou indisponible.</li>
            </ul>
          </div>
          <p>
            Le ministère publie des informations sur le développement des infrastructures, mais leur nombre
            national ne décrit pas votre routine. Pour décider, les trois bornes autour de vos lieux de vie
            comptent davantage que le total français.
          </p>
        </>
      ),
    },
    {
      id: 'calcul',
      title: 'Le calcul à faire avant de regarder les annonces',
      content: (
        <>
          <p>
            Sur une semaine habituelle, notez chaque trajet, sa distance et l’endroit où la voiture dort.
            Ajoutez les recharges réellement disponibles, pas celles qui seraient possibles « de temps en
            temps ». Estimez ensuite la part des kilomètres qui pourrait être faite en électrique sur une
            semaine d’hiver et sur une semaine d’été.
          </p>
          <p>
            Comparez au minimum quatre scénarios : essence sobre, hybride non rechargeable, hybride
            rechargeable avec votre taux de recharge réaliste, et électrique si une solution de recharge
            existe. Prix d’achat, énergie, assurance, entretien probable et valeur de revente doivent être
            regardés sur la même durée. Les valeurs futures restent des estimations, pas des garanties.
          </p>
          <p>
            L’outil gratuit <a href="/outils/orientation-motorisation/">d’orientation des motorisations</a>
            vous aide à poser ce premier diagnostic à partir de vos kilomètres et de votre recharge.
          </p>
        </>
      ),
    },
    {
      id: 'exemple-semaine',
      title: 'Exemple : transformer une semaine type en part électrique réaliste',
      content: (
        <>
          <p>
            Prenons un conducteur qui parcourt 15 000 km par an, dont cinq allers-retours domicile-travail de
            30 km pendant 46 semaines. Ces trajets représentent 6 900 km. Si le véhicule peut être rechargé au
            travail quatre jours sur cinq et si chaque journée tient réellement dans l’autonomie électrique,
            le potentiel maximal lié à ce motif est d’environ 5 520 km, avant les absences, l’hiver et les
            détours. Cela représente déjà moins de 40 % du kilométrage annuel.
          </p>
          <p>
            Ce calcul n’annonce ni consommation ni économie. Il révèle simplement que les 9 480 km restants
            devront être décrits : sont-ils constitués de petits trajets rechargeables le week-end, ou surtout
            d’autoroute batterie vide ? Deux personnes affichant le même kilométrage annuel peuvent donc obtenir
            des conclusions opposées.
          </p>
          <aside className="as-article-note as-note-estimate">
            <strong>Estimation explicite</strong>
            <p>
              30 km × 5 jours × 46 semaines = 6 900 km de domicile-travail. Avec quatre journées rechargeables
              sur cinq, 6 900 × 80 % = 5 520 km potentiellement électriques. L’autonomie réelle, les congés et
              les imprévus peuvent réduire ce total.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'alternatives',
      title: 'Comparer le PHEV aux bonnes alternatives, pas à une seule voiture',
      content: (
        <>
          <p>
            Le choix binaire « PHEV ou diesel » enferme trop vite la recherche. Je garde au moins trois
            alternatives capables de remplir le même usage. Une essence sobre peut coûter moins cher à l’achat ;
            une hybride non rechargeable peut simplifier les trajets urbains ; une électrique peut devenir plus
            cohérente si une vraie recharge régulière existe. Les modèles précis et leur prix d’occasion peuvent
            renverser l’ordre théorique des technologies.
          </p>
          <div className="as-table-scroll" tabIndex={0} aria-label="Pistes à comparer selon la recharge disponible">
            <table>
              <thead><tr><th>Votre situation</th><th>Pistes à comparer</th><th>Point décisif</th></tr></thead>
              <tbody>
                <tr><td>Aucune recharge fiable</td><td>Essence sobre, hybride non rechargeable</td><td>Prix d’achat et consommation réelle</td></tr>
                <tr><td>Recharge quotidienne, trajets courts</td><td>Électrique, PHEV, hybride</td><td>Part annuelle réellement rechargeable</td></tr>
                <tr><td>Beaucoup d’autoroute</td><td>Essence, diesel selon usage, électrique adaptée</td><td>Coût et organisation des longs trajets</td></tr>
                <tr><td>Recharge publique seulement</td><td>Hybride, essence, électrique ou PHEV selon réseau</td><td>Fiabilité et coût complet des sessions</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            Une fois deux véhicules identifiés, utilisez le <a href="/outils/comparateur-cout-global/">comparateur
            de coût global</a> avec une consommation thermique prudente pour le PHEV et une part électrique
            séparée dans vos notes. L’outil simplifie le calcul ; il ne remplace pas une simulation détaillée
            lorsque les énergies diffèrent fortement.
          </p>
        </>
      ),
    },
    {
      id: 'occasion',
      title: 'En occasion : vérifier aussi la batterie et le câble',
      content: (
        <>
          <p>
            Sur une occasion, je demande la présence du câble adapté, l’historique d’entretien et les
            éventuels diagnostics de batterie disponibles. Je vérifie que la recharge démarre sur une
            borne compatible et que l’autonomie affichée est interprétée avec la température et les trajets
            précédents. Un chiffre au tableau de bord ne constitue pas un certificat de capacité.
          </p>
          <p>
            L’essai doit aussi comporter une portion batterie déchargée pour observer l’agrément et la
            consommation du moteur thermique. Cette vérification ne remplace pas une inspection physique
            ou un diagnostic par un professionnel lorsque le risque le justifie.
          </p>
        </>
      ),
    },
    {
      id: 'verdict-personnel',
      title: 'La grille de décision finale en sept questions',
      content: (
        <>
          <ol>
            <li>Combien de jours par semaine une recharge est-elle réellement disponible sans détour dédié ?</li>
            <li>Quelle part des kilomètres annuels peut tenir dans l’autonomie électrique prudente ?</li>
            <li>Quelle consommation le modèle réalise-t-il batterie vide sur vos longs trajets ?</li>
            <li>Quel est l’écart de prix d’achat avec une essence, une hybride et une électrique comparables ?</li>
            <li>Les devis d’assurance et les dimensions de pneus créent-ils un écart sensible ?</li>
            <li>Le câble, la recharge et les éléments d’entretien sont-ils documentés sur l’occasion visée ?</li>
            <li>Le choix reste-t-il acceptable si la valeur de revente future est moins bonne que prévu ?</li>
          </ol>
          <p>
            Si les deux premières réponses sont faibles, je sors généralement le PHEV de la shortlist. Si elles
            sont solides, je le garde mais je chiffre encore le véhicule précis. La technologie ouvre une piste ;
            elle ne dispense jamais de comparer le prix, la version et l’exemplaire.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'Glossaire — véhicule hybride rechargeable',
      publisher: 'ADEME Car Labelling',
      url: 'https://carlabelling.ademe.fr/index/glossaire',
    },
    {
      title: 'Avis de l’ADEME sur les voitures électriques et la recharge',
      publisher: 'ADEME',
      url: 'https://www.ademe.fr/presse/communique-national/mondial-de-lautomobile-lademe-publie-son-avis-sur-le-vehicule-electrique-une-batterie-de-taille-raisonnable-assure-une-pertinence-climatique-et-economique',
    },
    {
      title: 'Real-world CO₂ emissions from cars and vans using on-board fuel consumption data',
      publisher: 'Commission européenne',
      url: 'https://climate.ec.europa.eu/news-other-reads/news/first-commission-report-real-world-co2-emissions-cars-and-vans-using-data-board-fuel-consumption-2024-03-18_en',
    },
    {
      title: 'Développer les bornes de recharge pour véhicules électriques',
      publisher: 'Ministère de la Transition écologique',
      url: 'https://www.ecologie.gouv.fr/politiques-publiques/developper-bornes-recharge-vehicules-electriques',
    },
  ],
  faqs: [
    {
      question: 'Peut-on rouler avec un hybride rechargeable sans jamais le brancher ?',
      answer:
        'Oui, la voiture peut généralement rouler avec son moteur thermique, mais cela n’en fait pas un usage cohérent. La consommation et le coût doivent alors être comparés à ceux d’une essence ou d’une hybride non rechargeable.',
    },
    {
      question: 'Une recharge par semaine suffit-elle ?',
      answer:
        'Cela dépend des distances, mais une seule recharge hebdomadaire laisse souvent une grande partie des kilomètres au moteur thermique. Calculez la part électrique réelle plutôt que le nombre de recharges seul.',
    },
    {
      question: 'L’hybride non rechargeable est-il toujours préférable sans prise ?',
      answer:
        'Non, mais il mérite d’être comparé. Le bon choix dépend du kilométrage, des trajets, du prix d’achat, de l’assurance et de la durée de détention.',
    },
  ],
  relatedSlugs: ['essence-diesel-hybride-electrique', 'achat-credit-loa-lld', 'verifier-voiture-occasion'],
};
